import { DisclaimerModal } from '@/entities/study';
import { TitleComponent as TitleComponentImport } from '@/widgets/page-header';
import { DEVICE } from '@/shared/config';
import { CustomButton } from '@/shared/ui/components';
import { Col, Row } from 'antd';
import _ from 'lodash';
import { t } from 'ttag';
import StepsComponent from '../stepsComponent/stepsComponent';
import ConfirmCancelProcessModal from '../confirmCancelProcessModal';
import NewStudyStep1 from '../newStudyStep1/newStudyStep1';
import NewStudyStep2 from '../newStudyStep2/newStudyStep2';
import NewStudyStep3 from '../newStudyStep3/newStudyStep3';
import NewStudyStep4 from '../newStudyStep4/newStudyStep4';
import NewStudyStep5 from '../newStudyStep5/newStudyStep5';
import { useNewStudy } from './useNewStudy';
import './newStudy.scss';

const TitleComponent = TitleComponentImport as any;

/**
 * Screen for the multi-step create-study wizard, wrapping the whole flow in the disclaimer and cancel-process confirmation modals. Pulls all wizard state and handlers from useNewStudy, renders a titled page with a back link to /studies whose right buttons come from rightButtonsRenderer, a vertical StepsComponent driven by state.stepTitlesCurrent on the left and the current step body from stepContentRendered on the right, plus a Step n/total banner and a web-view CSS class when isWebView is set.
 *
 * @returns Page element for the create-study wizard.
 */
const NewStudyPage = () => {
  const {
    state,
    newStudyData,
    isUsingStudyBiotresList,
    newStudyStepRef,
    isWebView,
    handleClick,
    handleClickBack,
    onClickYesButton,
    onClickNoButton,
    onClickAgreeDisclaimerModal,
    onClickCancelDisclaimerModal,
    updateStepTitleBiofluxDirect,
    setIsLoadedData,
    setIsLoading,
    setIsLoadingDivisions,
    setIsDisabledNext,
    setIsLoadingDevice,
    setIsChangingFacility,
    handleNavigation,
  } = useNewStudy();

  /**
   * Builds the footer navigation buttons for one step of the create-study wizard. Returns null until state.isLoadedData is true, then switches on the step index: Next only for step 1, Previous plus a Next gated by state.isLoading and the divisions or device loading and facility-changing flags for steps 2 and 3, Previous plus a Start Study or Initiate Study button depending on whether the step 3 device model is ICOR for step 4, and Finish for step 5, every button delegating to handleNavigation.
   *
   * @param step - Index of the wizard step whose buttons are built.
   * @returns Button group element for the step, or null when data is still loading or the step is unknown.
   */
  const rightButtonsRenderer = (step: any) => {
    if (!state.isLoadedData) {
      return null;
    }

    switch (step) {
      case 1: {
        return (
          <div className="right-buttons">
            <CustomButton
              id="next-btn-step-1"
              styles={{ minWidth: 80 }}
              onClick={() => {
                handleNavigation(true);
              }}
              size="small"
            >
              {t`Next`}
            </CustomButton>
          </div>
        );
      }
      case 2: {
        return (
          <div className="right-buttons">
            <CustomButton
              id="previus-btn-step-2"
              type="secondary"
              size="small"
              onClick={() => {
                handleNavigation(false);
              }}
            >
              {t`Previous`}
            </CustomButton>
            <CustomButton
              id="next-btn-step-2"
              styles={{ minWidth: 80 }}
              size="small"
              loading={state.isLoading}
              disabled={state.isLoadingDivisions}
              onClick={() => {
                handleNavigation(true);
              }}
            >
              {t`Next`}
            </CustomButton>
          </div>
        );
      }
      case 3: {
        return (
          <div className="right-buttons">
            <CustomButton
              id="previous-btn-step-3"
              type="secondary"
              size="small"
              onClick={() => {
                handleNavigation(false);
              }}
            >
              {t`Previous`}
            </CustomButton>
            <CustomButton
              id="next-btn-step-3"
              styles={{ minWidth: 80 }}
              size="small"
              loading={state.isLoading}
              disabled={state.isDisabledNext || state.isLoadingDevice || state.isChangingFacility}
              onClick={() => {
                handleNavigation(true);
              }}
            >
              {t`Next`}
            </CustomButton>
          </div>
        );
      }
      case 4: {
        return (
          <div className="right-buttons">
            <CustomButton
              id="previous-btn-step-4"
              type="secondary"
              size="small"
              onClick={() => {
                handleNavigation(false);
              }}
            >
              {t`Previous`}
            </CustomButton>
            <CustomButton
              id="initiate-study-btn-step-4"
              size="small"
              onClick={() => {
                handleNavigation(true);
              }}
            >
              {newStudyData.current.step3.model === DEVICE.ICOR
                ? t`Start Study`
                : t`Initiate Study`}
            </CustomButton>
          </div>
        );
      }
      case 5: {
        return (
          <div className="right-buttons">
            <CustomButton
              id="finish-btn-step-5"
              styles={{ minWidth: 80 }}
              size="small"
              onClick={() => {
                handleNavigation(true);
              }}
            >
              {t`Finish`}
            </CustomButton>
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  /**
   * Selects and renders the body component for one step of the create-study wizard. Switches on the step index across NewStudyStep1 through NewStudyStep5, passing each the shared newStudyStepRef, its own slice of newStudyData, the countries list and the loading, validation and facility-change setters it needs, and derives isUsingBiotresDevice for step 2 from the step 3 device model, returning null for any other index.
   *
   * @param step - Index of the wizard step whose content is rendered.
   * @returns Step body element, or null for an unrecognised step index.
   */
  const stepContentRendered = (step: any) => {
    switch (step) {
      case 1: {
        return (
          <NewStudyStep1
            ref={newStudyStepRef}
            defaultData={newStudyData.current.step1}
            isLoadedData={state.isLoadedData}
            setIsLoadedData={setIsLoadedData}
            handleClick={handleClick}
          />
        );
      }
      case 2: {
        return (
          <NewStudyStep2
            ref={newStudyStepRef}
            defaultData={newStudyData.current.step2}
            countries={state.countries}
            handleClick={handleClick}
            isUsingBiofluxDirect={newStudyData.current.step3.isBiofluxDirect}
            isDeviceAssignmentPending={newStudyData.current.step3.isDeviceAssignmentPending}
            isUsingBiotresDevice={_.includes(newStudyData.current.step3.model, DEVICE.BIOTRES)}
            isLoadedData={state.isLoadedData}
            setIsLoadedData={setIsLoadedData}
            setIsLoading={setIsLoading}
            isLoadingDivisions={state.isLoadingDivisions}
            setIsLoadingDivisions={setIsLoadingDivisions}
          />
        );
      }
      case 3: {
        return (
          <NewStudyStep3
            ref={newStudyStepRef}
            defaultData={newStudyData.current.step3}
            countries={state.countries}
            handleClick={handleClick}
            updateStepTitleBiofluxDirect={updateStepTitleBiofluxDirect}
            email={newStudyData.current.step2.email}
            selectedCountry={newStudyData.current.step2.country}
            isLoadedData={state.isLoadedData}
            setIsLoadedData={setIsLoadedData}
            setIsLoading={setIsLoading}
            isDisabledNext={state.isDisabledNext}
            setIsDisabledNext={setIsDisabledNext}
            isLoadingDevice={state.isLoadingDevice}
            setIsLoadingDevice={setIsLoadingDevice}
            isChangingFacility={state.isChangingFacility}
            setIsChangingFacility={setIsChangingFacility}
          />
        );
      }
      case 4: {
        return (
          <NewStudyStep4
            ref={newStudyStepRef}
            defaultData={newStudyData.current.step4}
            isLoadedData={state.isLoadedData}
            setIsLoadedData={setIsLoadedData}
            handleClick={handleClick}
          />
        );
      }
      case 5: {
        return (
          <NewStudyStep5
            ref={newStudyStepRef}
            defaultData={newStudyData.current.step5}
            selectedCountry={newStudyData.current.step2.country}
            isUsingStudyBiotresList={isUsingStudyBiotresList.current}
            setIsLoadedData={setIsLoadedData}
            handleClick={handleClick}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div className={isWebView ? 'create-study-web-view' : ''}>
      <DisclaimerModal
        isOpen={state.isOpenDisclaimerModal}
        leftButtonName={t`Cancel`}
        onClickLeftButton={onClickCancelDisclaimerModal}
        rightButtonName={t`Agree`}
        isFilledRightButton
        onClickRightButton={onClickAgreeDisclaimerModal}
      />
      <ConfirmCancelProcessModal
        isOpen={state.isOpenConfirmStopProcessModal}
        onClickNoButton={onClickNoButton}
        onClickYesButton={onClickYesButton}
      />

      <div className="custom-page custom-page-has-only-title create-study-page animated fadeIn">
        <div className="top-container">
          <TitleComponent
            hasBackButton
            title={t`Create new study`}
            linkGoBack="/studies"
            rightButtons={rightButtonsRenderer(state.step)}
            handleClickBack={handleClickBack}
          />
        </div>
        <Row className="custom-body">
          <Col span="6" className="custom-body__left-container">
            <StepsComponent step={state.step} titles={state.stepTitlesCurrent} />
          </Col>
          <Col span="18" className="custom-body__right-container">
            {isWebView && (
              <div className="step-web-view">
                Step {state.step}/{state?.stepTitlesCurrent?.length}
              </div>
            )}
            {stepContentRendered(state.step)}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default NewStudyPage;
